'use client'

import { useId } from 'react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Cookie, X, ChevronDown, ChevronUp, Shield } from 'lucide-react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useCookieConsentBanner } from '@/hooks/use-cookie-consent'

// The choice and its effects live in the hook, shared with the Encuentro banner.
export {
  resetCookieConsent,
  useCookiePreferences,
  type CookiePreferences,
} from '@/hooks/use-cookie-consent'

export function CookieConsentBanner() {
  const {
    isVisible,
    showDetails,
    toggleDetails,
    preferences,
    setPreference,
    acceptAll: handleAcceptAll,
    rejectNonEssential: handleRejectNonEssential,
    savePreferences: handleSavePreferences,
    close: handleClose,
  } = useCookieConsentBanner()
  const t = useTranslations('landing')
  const tCommon = useTranslations('common')
  const detailsId = useId()

  if (!isVisible) return null

  return (
    <div
      data-slot="cookie-consent"
      className="fixed bottom-0 inset-x-0 z-50 p-4 animate-in slide-in-from-bottom duration-300"
    >
      <Card
        role="region"
        aria-label={t('cookies.title')}
        className="max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto mx-auto shadow-lg border-2"
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Cookie className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">{t('cookies.title')}</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              aria-label={tCommon('close')}
              className="h-11 w-11 shrink-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <CardDescription>{t('cookies.description')}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Expandable Details */}
          <button
            type="button"
            aria-expanded={showDetails}
            aria-controls={showDetails ? detailsId : undefined}
            onClick={toggleDetails}
            className="flex min-h-11 items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showDetails ? t('cookies.hideDetails') : t('cookies.showDetails')}
          </button>

          {showDetails && (
            <div id={detailsId} className="space-y-4 pt-2 border-t">
              {/* Essential Cookies */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-600" />
                    <Label className="font-medium">{t('cookies.essential')}</Label>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                      {t('cookies.alwaysOn')}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.essentialDescription')}
                  </p>
                </div>
                <Switch
                  checked
                  disabled
                  aria-label={t('cookies.essential')}
                  className="opacity-50"
                />
              </div>

              {/* Functional Cookies */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="functional" className="font-medium">
                    {t('cookies.functional')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.functionalDescription')}
                  </p>
                </div>
                <Switch
                  id="functional"
                  checked={preferences.functional}
                  onCheckedChange={(checked) => setPreference('functional', checked)}
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="analytics" className="font-medium">
                    {t('cookies.analytics')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.analyticsDescription')}
                  </p>
                </div>
                <Switch
                  id="analytics"
                  checked={preferences.analytics}
                  onCheckedChange={(checked) => setPreference('analytics', checked)}
                />
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex flex-col sm:flex-row gap-2 pt-2">
          {showDetails ? (
            <>
              <Button
                variant="outline"
                onClick={handleRejectNonEssential}
                className="w-full sm:w-auto"
              >
                {t('cookies.essentialOnly')}
              </Button>
              <Button onClick={handleSavePreferences} className="w-full sm:w-auto">
                {t('cookies.savePreferences')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleRejectNonEssential}
                className="w-full sm:w-auto"
              >
                {t('cookies.rejectNonEssential')}
              </Button>
              <Button onClick={handleAcceptAll} className="w-full sm:w-auto">
                {t('cookies.acceptAll')}
              </Button>
            </>
          )}
          <Link
            href="/policies/privacy"
            target="_blank"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors text-center sm:ml-auto"
          >
            {t('cookies.privacyPolicy')}
          </Link>
        </CardFooter>
      </Card>
    </div>
  )
}
